import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StoreForm from '../../components/admin/StoreForm';
import Toast from '../../components/ui/Toast';
import api from '../../utils/api';

const StoreFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(id ? true : false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    if (id) {
      fetchStore();
    }
  }, [id]);

  const fetchStore = async () => {
    try {
      setFetching(true);
      const response = await api.get(`/stores/${id}`);
      if (response.data.success) {
        setStore(response.data.data);
      } else {
        showToast(response.data.message || 'Failed to load store details', 'error');
        navigate('/admin/stores');
      }
    } catch (error) {
      console.error('Error fetching store:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load store details';
      showToast(errorMessage, 'error');
      navigate('/admin/stores');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setLoading(true);
      let submitData;
      if (id && store) {
        // Only send changed fields
        submitData = new FormData();
        Object.keys(formData).forEach(key => {
          if (key === 'icon' && !formData.icon) return;
          if (key === 'remove_icon' && formData.remove_icon) {
            submitData.append('remove_icon', '1');
            return;
          }
          if (formData[key] !== store[key] && key !== 'remove_icon') {
            submitData.append(key, formData[key]);
          }
        });
      } else {
        // For create, send all fields
        submitData = new FormData();
        Object.keys(formData).forEach(key => {
          if (formData[key] !== null) {
            submitData.append(key, formData[key]);
          }
        });
      }

      if (id) {
        // Update existing store using POST with _method: 'put'
        submitData.append('_method', 'put');
        const response = await api.post(`/stores/${id}`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response.data.success) {
          showToast(response.data.message || 'Store updated successfully', 'success');
        } else {
          throw new Error(response.data.message || 'Failed to update store');
        }
      } else {
        // Create new store
        const response = await api.post('/stores', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response.data.success) {
          showToast(response.data.message || 'Store created successfully', 'success');
        } else {
          throw new Error(response.data.message || 'Failed to create store');
        }
      }
      // Redirect to stores list after a short delay
      setTimeout(() => {
        navigate('/admin/stores');
      }, 1500);
    } catch (error) {
      console.error('Error saving store:', error);
      if (error.response?.data?.errors) {
        throw error; // Let the form component handle validation errors
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save store';
        showToast(errorMessage, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading store details...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StoreForm
        store={store}
        onSubmit={handleSubmit}
        isLoading={loading}
      />
      
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={closeToast}
      />
    </div>
  );
};

export default StoreFormPage; 